import React, { useState } from 'react';
import { CheckCircle2, Download, Trash2, ExternalLink, Facebook, Linkedin, Edit2, MessageSquare, ChevronDown, ChevronUp, AlertCircle, FileText, Search, User, Send, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PeopleTab } from './PeopleTab';
import { FinancialsTab } from './FinancialsTab';
import { DetailsTab } from './DetailsTab';
import { NotificationsTab } from './NotificationsTab';
import { OverviewTab } from './OverviewTab';
export function CreditApplication() {
  const [activeTab, setActiveTab] = useState('OVERVIEW');
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<null | 'approve' | 'approveWithChanges' | 'reject'>(null);
  const [decisionMade, setDecisionMade] = useState<null | 'approved' | 'approvedWithChanges' | 'rejected'>(null);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [expandedRef, setExpandedRef] = useState<string | null>('Weyerhaeuser Paper');
  const handleDecision = (decision: 'approved' | 'approvedWithChanges' | 'rejected') => {
    setDecisionMade(decision);
    setActiveModal(null);
    setShowAuditTrail(true);
  };
  return <div className="min-h-full bg-gray-50 p-6" data-id="element-406">
      <div className="max-w-7xl mx-auto space-y-6" data-id="element-407">
        {/* Header Card */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4" data-id="element-408">
          <div className="flex items-center justify-between gap-4" data-id="element-409">
            {/* Left: Company Info + Notes */}
            <div className="shrink-0" data-id="element-410">
              <h1 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-411">
                DUNDER MIFFLIN PAPER COMPANY
              </h1>
              <div className="flex items-center gap-3 text-indigo-600 mb-2" data-id="element-412">
                <ExternalLink className="w-3.5 h-3.5 cursor-pointer" data-id="element-413" />
                <Facebook className="w-3.5 h-3.5 cursor-pointer" data-id="element-414" />
                <Linkedin className="w-3.5 h-3.5 cursor-pointer" data-id="element-415" />
                <Edit2 className="w-3.5 h-3.5 cursor-pointer" data-id="element-416" />
              </div>
              <div className="space-y-0.5 text-xs text-gray-600 mb-2" data-id="element-417">
                <div className="flex items-center gap-1.5" data-id="element-418">
                  <span className="font-semibold text-gray-900" data-id="element-419">
                    Credit Manager:
                  </span>
                  <span data-id="element-420">Dave Davidson</span>
                  <Edit2 className="w-3 h-3 text-indigo-600 cursor-pointer" data-id="element-421" />
                </div>
                <div className="flex items-center gap-1.5" data-id="element-422">
                  <span className="font-semibold text-gray-900" data-id="element-423">Added:</span>
                  <span data-id="element-424">3/27/2026</span>
                </div>
              </div>
              <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1 rounded flex items-center gap-1.5 text-xs font-medium" data-id="element-425">
                <MessageSquare className="w-3 h-3" data-id="element-426" />
                NOTES
              </button>
            </div>

            {/* Right: Risk Gauge */}
            <div className="flex flex-col items-center shrink-0 mr-4" data-id="element-427">
              <div className="flex items-end gap-2 mb-1" data-id="element-428">
                <span className="text-xs text-gray-400 font-medium" data-id="element-429">0</span>
                <div className="relative w-64" data-id="element-430">
                  {/* Score label */}
                  <div className="absolute -top-6 left-[82%] -translate-x-1/2 bg-green-100 text-green-800 text-xs font-bold px-2 py-0.5 rounded" data-id="element-431">
                    82
                  </div>
                  {/* Bar */}
                  <div className="relative w-full h-6 flex rounded-full overflow-hidden" data-id="element-432">
                    <div className="w-1/3 bg-red-300" data-id="element-433"></div>
                    <div className="w-1/3 bg-yellow-300" data-id="element-434"></div>
                    <div className="w-1/3 bg-green-300" data-id="element-435"></div>
                  </div>
                  {/* Indicator line */}
                  <div className="absolute top-0 bottom-0 left-[82%] w-0.5 flex flex-col items-center" data-id="element-436">
                    <div className="w-[3px] h-full bg-gray-700 rounded-full" style={{
                    borderLeft: '2px dashed #374151'
                  }} data-id="element-437"></div>
                  </div>
                </div>
                <span className="text-xs text-gray-400 font-medium" data-id="element-438">100</span>
              </div>
              <div className="w-64 flex justify-between px-0 mt-1" data-id="element-439">
                <span className="text-xs text-red-500 font-medium pl-7" data-id="element-440">
                  Low
                </span>
                <span className="text-xs text-yellow-600 font-medium" data-id="element-441">
                  Medium
                </span>
                <span className="text-xs text-gray-600 font-medium pr-7" data-id="element-442">
                  High
                </span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-t border-gray-200 mt-4 pt-3" data-id="element-443">
            {['OVERVIEW', 'NOTIFICATIONS', 'DETAILS', 'FINANCIALS', 'PEOPLE', 'APPLICATIONS'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 text-center py-1.5 text-xs font-semibold ${tab === activeTab ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-444">
                {tab}
              </button>)}
          </div>
        </div>

        {/* Credit Decision Section */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg shadow-sm overflow-hidden" data-id="element-445">
          {/* Header Row */}
          <button onClick={() => setDecisionOpen(!decisionOpen)} className="w-full p-4 flex items-center justify-between bg-indigo-100 hover:bg-indigo-150 transition-colors" data-id="element-446">
            <div className="flex items-center gap-4" data-id="element-447">
              <span className="font-bold text-gray-900" data-id="element-448">Credit Decision</span>
              <span className="border border-green-500 text-green-600 px-2.5 py-0.5 rounded-full text-sm font-medium" data-id="element-449">
                Ready
              </span>
              <span className="text-sm text-gray-500" data-id="element-450">
                Date: March 27, 2026
              </span>
              <span className="text-sm text-gray-300" data-id="element-451">·</span>
              <span className="text-sm font-medium text-gray-700" data-id="element-452">
                Recommended Credit Limit: $50,000
              </span>
            </div>
            {decisionOpen ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-453" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-454" />}
          </button>

          {/* Expanded Content */}
          {decisionOpen && <div className="p-6 border-t border-indigo-200" data-id="element-455">
              <div className="grid grid-cols-2 gap-x-12 gap-y-6 mb-8" data-id="element-456">
                {/* Left Column */}
                <div className="space-y-4" data-id="element-457">
                  <div data-id="element-458">
                    <p className="text-sm text-gray-500 mb-1" data-id="element-459">
                      Recommended Decision
                    </p>
                    <p className="text-xl font-bold text-gray-900" data-id="element-460">
                      Approve for $50,000
                    </p>
                  </div>
                  <div data-id="element-461">
                    <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-462">
                      Terms
                    </p>
                    <div className="text-sm text-gray-600 space-y-0.5" data-id="element-463">
                      <p data-id="element-464">Payment Terms Days: 30</p>
                      <p data-id="element-465">Discount: 5%</p>
                      <p data-id="element-466">Discount Days: 10</p>
                      <p data-id="element-467">Late Pay Penalty: 1.5%</p>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4" data-id="element-468">
                  <div data-id="element-469">
                    <div className="flex items-center gap-2 mb-1" data-id="element-470">
                      <p className="text-sm text-gray-500" data-id="element-471">Policy Applied</p>
                      <span className="text-sm text-gray-300" data-id="element-472">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-473">
                        (View Policy)
                      </button>
                    </div>
                    <p className="font-bold text-gray-900" data-id="element-474">
                      My Company's Credit Policy v1
                    </p>
                  </div>

                  <div data-id="element-475">
                    <div className="flex items-center gap-2 mb-1" data-id="element-476">
                      <p className="text-sm text-gray-500" data-id="element-477">
                        Credit Application
                      </p>
                      <span className="text-sm text-gray-300" data-id="element-478">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-479">
                        (View Application)
                      </button>
                    </div>
                    <p className="text-sm text-gray-700" data-id="element-480">
                      Submitted on: March 27, 2026
                    </p>
                  </div>

                  <div data-id="element-481">
                    <div className="flex items-center gap-2 mb-1" data-id="element-482">
                      <p className="text-sm text-gray-500" data-id="element-483">
                        Recommendation Summary
                      </p>
                      <span className="text-sm text-gray-300" data-id="element-484">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-485">
                        (Full Reasoning)
                      </button>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed" data-id="element-486">
                      Dunder Mifflin Paper Company, Inc. is approved for credit
                      based on strong business fundamentals, an established
                      77-year operating history, and positive trade references.
                      However, the credit limit is conservatively set due to the
                      applicant's limited initial order size and the need to
                      establish a direct payment history, warranting a measured
                      initial credit exposure.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-4 gap-4" data-id="element-487">
                <button onClick={() => setActiveModal('approve')} className="bg-green-700 hover:bg-green-800 text-white py-3 rounded-lg font-semibold text-sm" data-id="element-488">
                  APPROVE
                </button>
                <button onClick={() => setActiveModal('approveWithChanges')} className="bg-yellow-600 hover:bg-yellow-700 text-white py-3 rounded-lg font-semibold text-sm" data-id="element-489">
                  APPROVE WITH CHANGES
                </button>
                <button onClick={() => setActiveModal('reject')} className="bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold text-sm" data-id="element-490">
                  REJECT
                </button>
                <button className="border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 py-3 rounded-lg font-semibold text-sm" data-id="element-491">
                  REGENERATE RECOMMENDATION
                </button>
              </div>
            </div>}
        </div>

        {/* Decision Modals */}
        <AnimatePresence data-id="element-492">
          {activeModal && <>
              {/* Backdrop */}
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setActiveModal(null)} className="fixed inset-0 bg-black/50 z-50" data-id="element-493" />

              {/* Modal */}
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-494">
                <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-8" data-id="element-495">
                  {activeModal === 'approve' && <>
                      <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-496">
                        Approve Recommendation
                      </h2>
                      <div className="space-y-6" data-id="element-497">
                        <div className="grid grid-cols-3 gap-4" data-id="element-498">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3 col-span-2" data-id="element-499">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-500">
                              Credit Limit
                            </legend>
                            <input type="text" defaultValue="50,000" className="w-full text-lg text-gray-900 outline-none" data-id="element-501" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-502">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-503">
                              Currency
                            </legend>
                            <select className="w-full text-lg text-gray-900 outline-none bg-transparent" data-id="element-504">
                              <option data-id="element-505">USD</option>
                            </select>
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-506">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-507">
                            Payment Terms Days
                          </legend>
                          <input type="text" defaultValue="30" className="w-full text-lg text-gray-900 outline-none" data-id="element-508" />
                        </fieldset>
                        <div className="grid grid-cols-2 gap-4" data-id="element-509">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-510">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-511">
                              Discount (%)
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-512" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-513">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-514">
                              Discount Days
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-515" />
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-516">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-517">
                            Late Pay Penalty (%)
                          </legend>
                          <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-518" />
                        </fieldset>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-519">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-520">
                            Reasoning
                          </legend>
                          <textarea rows={4} className="w-full text-lg text-gray-900 outline-none resize-none" data-id="element-521" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-4 mt-8" data-id="element-522">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-6 py-3 hover:bg-indigo-50 rounded" data-id="element-523">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('approved')} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-3 rounded" data-id="element-524">
                          APPROVE
                        </button>
                      </div>
                    </>}

                  {activeModal === 'approveWithChanges' && <>
                      <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-525">
                        Approve and Input Values
                      </h2>
                      <div className="space-y-6" data-id="element-526">
                        <div className="grid grid-cols-3 gap-4" data-id="element-527">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3 col-span-2" data-id="element-528">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-529">
                              Credit Limit *
                            </legend>
                            <input type="text" defaultValue="50,000" className="w-full text-lg text-gray-900 outline-none" data-id="element-530" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-531">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-532">
                              Currency
                            </legend>
                            <select className="w-full text-lg text-gray-900 outline-none bg-transparent" data-id="element-533">
                              <option data-id="element-534">USD</option>
                            </select>
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-535">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-536">
                            Payment Terms Days
                          </legend>
                          <input type="text" defaultValue="30" className="w-full text-lg text-gray-900 outline-none" data-id="element-537" />
                        </fieldset>
                        <div className="grid grid-cols-2 gap-4" data-id="element-538">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-539">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-540">
                              Discount (%)
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-541" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-542">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-543">
                              Discount Days
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-544" />
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-545">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-546">
                            Late Pay Penalty (%)
                          </legend>
                          <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-547" />
                        </fieldset>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-548">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-549">
                            Reasoning
                          </legend>
                          <textarea rows={4} className="w-full text-lg text-gray-900 outline-none resize-none" data-id="element-550" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-4 mt-8" data-id="element-551">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-6 py-3 hover:bg-indigo-50 rounded" data-id="element-552">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('approvedWithChanges')} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-3 rounded" data-id="element-553">
                          CONFIRM APPROVAL
                        </button>
                      </div>
                    </>}

                  {activeModal === 'reject' && <>
                      <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-554">
                        Reject
                      </h2>
                      <div className="space-y-6" data-id="element-555">
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-556">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-557">
                            Reasoning
                          </legend>
                          <textarea rows={6} className="w-full text-lg text-gray-900 outline-none resize-none" data-id="element-558" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-4 mt-8" data-id="element-559">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-6 py-3 hover:bg-indigo-50 rounded" data-id="element-560">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('rejected')} className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3 rounded" data-id="element-561">
                          CONFIRM REJECTION
                        </button>
                      </div>
                    </>}
                </div>
              </motion.div>
            </>}
        </AnimatePresence>

        {/* Audit Trail */}
        <AnimatePresence data-id="element-562">
          {showAuditTrail && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-563">
              <div className="p-6" data-id="element-564">
                <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-565">
                  Audit Trail
                </h3>
                <div className="space-y-6" data-id="element-566">
                  {/* Application Submitted */}
                  <div className="flex gap-4" data-id="element-567">
                    <div className="flex flex-col items-center" data-id="element-568">
                      <div className="w-3 h-3 rounded-full bg-blue-500 mt-1.5" data-id="element-569" />
                      <div className="w-0.5 h-full bg-gray-200 mt-2" data-id="element-570" />
                    </div>
                    <div className="flex-1 pb-6" data-id="element-571">
                      <div className="flex items-center gap-2 mb-1" data-id="element-572">
                        <h4 className="font-semibold text-gray-900" data-id="element-573">
                          Application Submitted
                        </h4>
                        <span className="text-xs text-gray-500" data-id="element-574">
                          March 27, 2026, 10:15 AM
                        </span>
                      </div>
                      <p className="text-sm text-gray-600" data-id="element-575">by Pam Beesly</p>
                    </div>
                  </div>

                  {/* Assigned to Credit Manager */}
                  <div className="flex gap-4" data-id="element-576">
                    <div className="flex flex-col items-center" data-id="element-577">
                      <div className="w-3 h-3 rounded-full bg-blue-500 mt-1.5" data-id="element-578" />
                      <div className="w-0.5 h-full bg-gray-200 mt-2" data-id="element-579" />
                    </div>
                    <div className="flex-1 pb-6" data-id="element-580">
                      <div className="flex items-center gap-2 mb-1" data-id="element-581">
                        <h4 className="font-semibold text-gray-900" data-id="element-582">
                          Assigned to Credit Manager
                        </h4>
                        <span className="text-xs text-gray-500" data-id="element-583">
                          March 27, 2026, 10:16 AM
                        </span>
                      </div>
                      <p className="text-sm text-gray-600" data-id="element-584">
                        assigned to Dave Davidson
                      </p>
                    </div>
                  </div>

                  {/* Decision Entry */}
                  <div className="flex gap-4" data-id="element-585">
                    <div className="flex flex-col items-center" data-id="element-586">
                      <div className={`w-3 h-3 rounded-full mt-1.5 ${decisionMade === 'approved' ? 'bg-green-500' : decisionMade === 'approvedWithChanges' ? 'bg-yellow-500' : 'bg-red-500'}`} data-id="element-587" />
                    </div>
                    <div className="flex-1" data-id="element-588">
                      <div className="flex items-center gap-2 mb-1" data-id="element-589">
                        <h4 className="font-semibold text-gray-900" data-id="element-590">
                          {decisionMade === 'approved' && 'Credit Approved for $50,000'}
                          {decisionMade === 'approvedWithChanges' && 'Credit Approved with Modifications'}
                          {decisionMade === 'rejected' && 'Credit Application Rejected'}
                        </h4>
                        <span className="text-xs text-gray-500" data-id="element-591">
                          March 29, 2026, 2:34 PM
                        </span>
                      </div>
                      <p className="text-sm text-gray-600" data-id="element-592">by Dave Davidson</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {activeTab === 'OVERVIEW' ? <OverviewTab data-id="element-593" /> : activeTab === 'FINANCIALS' ? <FinancialsTab data-id="element-594" /> : activeTab === 'PEOPLE' ? <PeopleTab data-id="element-595" /> : activeTab === 'DETAILS' ? <DetailsTab data-id="element-596" /> : activeTab === 'NOTIFICATIONS' ? <NotificationsTab data-id="element-597" /> : activeTab === 'APPLICATIONS' ? <div className="bg-white rounded-lg border border-gray-200 shadow-sm" data-id="element-598">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex justify-between items-start" data-id="element-599">
              <div data-id="element-600">
                <div className="flex items-center gap-3 mb-2" data-id="element-601">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-602">
                    Credit Application
                  </h2>
                  <span className="border border-indigo-200 text-indigo-700 bg-indigo-50 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5" data-id="element-603">
                    <Search className="w-3.5 h-3.5" data-id="element-604" />
                    Ready for Review
                  </span>
                </div>
                <div className="text-sm text-gray-600 space-y-1" data-id="element-605">
                  <p data-id="element-606">
                    <span className="font-semibold text-gray-900" data-id="element-607">
                      Started On:
                    </span>{' '}
                    March 27, 2026
                  </p>
                  <p data-id="element-608">
                    <span className="font-semibold text-gray-900" data-id="element-609">
                      Submitted On:
                    </span>{' '}
                    March 27, 2026
                  </p>
                </div>
              </div>
              <button className="border border-indigo-200 text-indigo-600 hover:bg-indigo-50 px-4 py-2 rounded text-sm font-medium flex items-center gap-2" data-id="element-610">
                <Download className="w-4 h-4" data-id="element-611" />
                DOWNLOAD PDF
              </button>
            </div>

            {/* Applicant Details */}
            <div className="p-6 border-b border-gray-200" data-id="element-612">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-613">
                Applicant Details
              </h3>
              <div className="grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-614">
                <div data-id="element-615">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-616">Name</p>
                  <p className="text-gray-900" data-id="element-617">Pam Beesly</p>
                </div>
                <div data-id="element-618">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-619">Company</p>
                  <p className="text-gray-900" data-id="element-620">
                    Dunder Mifflin Paper Company Inc
                  </p>
                </div>
                <div data-id="element-621">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-622">Role</p>
                  <p className="text-gray-900" data-id="element-623">Office Administrator</p>
                </div>
                <div data-id="element-624">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-625">Department</p>
                  <p className="text-gray-900" data-id="element-626">Administration</p>
                </div>
                <div data-id="element-627">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-628">Email</p>
                  <div className="flex items-center gap-2 mb-3" data-id="element-629">
                    <p className="text-gray-900" data-id="element-630">
                      pam.beesly@dundermifflin.com
                    </p>
                    <CheckCircle2 className="w-4 h-4 text-green-500" data-id="element-631" />
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3" data-id="element-632">
                    <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" data-id="element-633" />
                    <div data-id="element-634">
                      <p className="font-semibold text-gray-900 text-sm" data-id="element-635">
                        Email Verification
                      </p>
                      <p className="text-sm text-gray-700 mt-1" data-id="element-636">
                        Email verified and matches company domain.
                      </p>
                    </div>
                  </div>
                </div>
                <div data-id="element-637">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-638">Phone Number</p>
                  <p className="text-gray-900" data-id="element-639">(570) 555-0126</p>
                </div>
                <div data-id="element-640">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-641">
                    Location (IP Address)
                  </p>
                  <div className="flex items-center gap-2 mb-3" data-id="element-642">
                    <p className="text-gray-900" data-id="element-643">
                      Scranton, PA (136.61.36.131)
                    </p>
                    <CheckCircle2 className="w-4 h-4 text-green-500" data-id="element-644" />
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3" data-id="element-645">
                    <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" data-id="element-646" />
                    <div data-id="element-647">
                      <p className="font-semibold text-gray-900 text-sm" data-id="element-648">
                        IP Address Verification
                      </p>
                      <p className="text-sm text-gray-700 mt-1" data-id="element-649">
                        Applicant location matches company headquarters.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Contact Information */}
            <div className="p-6 border-b border-gray-200" data-id="element-650">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-651">
                Business Contact Information
              </h3>
              <div className="grid grid-cols-2 gap-12" data-id="element-652">
                <div data-id="element-653">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-654">
                    Legal Company Name
                  </p>
                  <p className="text-gray-900" data-id="element-655">
                    Dunder Mifflin Paper Company Inc
                  </p>
                </div>
                <div data-id="element-656">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-657">
                    Please attach W-9 Form below:
                  </p>
                  <div className="border border-indigo-200 rounded px-3 py-2 flex items-center gap-2 text-indigo-600 text-sm max-w-xs bg-white" data-id="element-658">
                    <FileText className="w-4 h-4" data-id="element-659" />
                    W9.svg
                  </div>
                </div>
              </div>
            </div>

            {/* Physical Address */}
            <div className="p-6 border-b border-gray-200" data-id="element-660">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-661">
                Physical Address
              </h3>
              <div className="grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-662">
                <div data-id="element-663">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-664">Street Address</p>
                  <p className="text-gray-900" data-id="element-665">1725 Slough Avenue</p>
                </div>
                <div data-id="element-666">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-667">City</p>
                  <p className="text-gray-900" data-id="element-668">Scranton</p>
                </div>
                <div data-id="element-669">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-670">State</p>
                  <p className="text-gray-900" data-id="element-671">PA</p>
                </div>
                <div data-id="element-672">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-673">Zip Code</p>
                  <p className="text-gray-900" data-id="element-674">18505</p>
                </div>
                <div data-id="element-675">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-676">Country</p>
                  <p className="text-gray-900" data-id="element-677">United States</p>
                </div>
              </div>
            </div>

            {/* Principals */}
            <div className="p-6 border-b border-gray-200" data-id="element-678">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-679">
                Principals
              </h3>
              <div className="grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-680">
                <div data-id="element-681">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-682">Full Name</p>
                  <p className="text-gray-900" data-id="element-683">Michael Scott</p>
                </div>
                <div data-id="element-684">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-685">Address</p>
                  <p className="text-gray-900" data-id="element-686">1725 Slough Avenue</p>
                </div>
                <div data-id="element-687">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-688">Position</p>
                  <p className="text-gray-900" data-id="element-689">Regional Manager</p>
                </div>
                <div data-id="element-690">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-691">Phone #</p>
                  <p className="text-gray-900" data-id="element-692">(570) 555-0126</p>
                </div>
              </div>
            </div>

            {/* Business Details */}
            <div className="p-6 border-b border-gray-200" data-id="element-693">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-694">
                Business Details
              </h3>
              <div className="grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-695">
                <div data-id="element-696">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-697">
                    Date Business Started
                  </p>
                  <p className="text-gray-900" data-id="element-698">1949-01-01</p>
                </div>
                <div data-id="element-699">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-700">Company Website</p>
                  <p className="text-gray-900" data-id="element-701">https://www.dundermifflin.com</p>
                </div>
                <div data-id="element-702">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-703">Business Type</p>
                  <p className="text-gray-900" data-id="element-704">Corporation</p>
                </div>
                <div data-id="element-705">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-706">
                    If "Other", please specify:
                  </p>
                  <p className="text-gray-900" data-id="element-707"></p>
                </div>
                <div data-id="element-708">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-709">
                    Accounting Contact Name
                  </p>
                  <p className="text-gray-900" data-id="element-710">Angela Martin</p>
                </div>
                <div data-id="element-711">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-712">
                    Accounting Contact Email
                  </p>
                  <p className="text-gray-900" data-id="element-713">
                    angela.martin@dundermifflin.com
                  </p>
                </div>
              </div>
            </div>

            {/* Trade References */}
            <div className="p-6 border-b border-gray-200" data-id="element-714">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-715">
                Trade References
              </h3>
              <div className="space-y-4 mb-6" data-id="element-716">
                {/* Weyerhaeuser Paper - Pending */}
                <div className="border border-gray-200 rounded-lg bg-white overflow-hidden" data-id="element-717">
                  <button onClick={() => setExpandedRef(expandedRef === 'Weyerhaeuser Paper' ? null : 'Weyerhaeuser Paper')} className="w-full p-4 flex justify-between items-center hover:bg-gray-50 transition-colors text-left" data-id="element-718">
                    <div data-id="element-719">
                      <h4 className="text-lg font-bold text-gray-900 mb-1.5" data-id="element-720">
                        Weyerhaeuser Paper
                      </h4>
                      <div className="flex items-center gap-4 text-sm text-gray-500" data-id="element-721">
                        <span className="flex items-center gap-1.5" data-id="element-722">
                          <Calendar className="w-4 h-4" data-id="element-723" />
                          Pending since March 28, 2026
                        </span>
                        <span className="flex items-center gap-1.5" data-id="element-724">
                          <MessageSquare className="w-4 h-4" data-id="element-725" />1 email sent
                        </span>
                      </div>
                    </div>
                    {expandedRef === 'Weyerhaeuser Paper' ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-726" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-727" />}
                  </button>
                  <AnimatePresence data-id="element-728">
                    {expandedRef === 'Weyerhaeuser Paper' && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="border-t border-gray-100" data-id="element-729">
                        <div className="p-6 space-y-6" data-id="element-730">
                          <div data-id="element-731">
                            <div className="flex items-center gap-2 mb-4" data-id="element-732">
                              <User className="w-5 h-5 text-gray-700" data-id="element-733" />
                              <h5 className="font-bold text-gray-900" data-id="element-734">
                                Reference Information
                              </h5>
                              <Edit2 className="w-4 h-4 text-indigo-600 cursor-pointer" data-id="element-735" />
                            </div>
                            <div className="bg-gray-50 rounded-lg p-6 grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-736">
                              <div data-id="element-737">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-738">
                                  Company Name
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-739">
                                  Weyerhaeuser Paper
                                </p>
                              </div>
                              <div data-id="element-740">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-741">
                                  Contact Name
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-742">
                                  Todd Packer
                                </p>
                              </div>
                              <div data-id="element-743">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-744">
                                  Contact Phone Number
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-745">
                                  (570) 555-0142
                                </p>
                              </div>
                              <div data-id="element-746">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-747">
                                  Contact Work Email
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-748">
                                  t.packer@weyerhaeuser.com
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-6" data-id="element-749">
                            <h5 className="font-bold text-gray-900 mb-2" data-id="element-750">
                              Awaiting Response
                            </h5>
                            <p className="text-sm text-gray-600 mb-4" data-id="element-751">
                              The reference request has been sent 1 times.
                            </p>
                            <button className="flex items-center gap-2 px-4 py-2 border border-indigo-300 text-indigo-600 bg-white hover:bg-indigo-50 rounded-md text-sm font-medium transition-colors" data-id="element-752">
                              <Send className="w-4 h-4" data-id="element-753" />
                              SEND REMINDER
                            </button>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                {/* Hammermill - Completed */}
                <div className="border border-gray-200 rounded-lg bg-white overflow-hidden" data-id="element-754">
                  <button onClick={() => setExpandedRef(expandedRef === 'Hammermill' ? null : 'Hammermill')} className="w-full p-4 flex justify-between items-center hover:bg-gray-50 transition-colors text-left" data-id="element-755">
                    <div data-id="element-756">
                      <h4 className="text-lg font-bold text-gray-900 mb-1.5" data-id="element-757">
                        Hammermill
                      </h4>
                      <div className="flex items-center gap-4 text-sm text-gray-500" data-id="element-758">
                        <span className="flex items-center gap-1.5" data-id="element-759">
                          <Calendar className="w-4 h-4" data-id="element-760" />
                          Completed on March 27, 2026
                        </span>
                        <span className="flex items-center gap-1.5" data-id="element-761">
                          <MessageSquare className="w-4 h-4" data-id="element-762" />1 email sent
                        </span>
                      </div>
                    </div>
                    {expandedRef === 'Hammermill' ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-763" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-764" />}
                  </button>
                  <AnimatePresence data-id="element-765">
                    {expandedRef === 'Hammermill' && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="border-t border-gray-100" data-id="element-766">
                        <div className="p-6 space-y-6" data-id="element-767">
                          <div data-id="element-768">
                            <div className="flex items-center gap-2 mb-4" data-id="element-769">
                              <User className="w-5 h-5 text-gray-700" data-id="element-770" />
                              <h5 className="font-bold text-gray-900" data-id="element-771">
                                Reference Information
                              </h5>
                              <Edit2 className="w-4 h-4 text-indigo-600 cursor-pointer" data-id="element-772" />
                            </div>
                            <div className="bg-gray-50 rounded-lg p-6 grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-773">
                              <div data-id="element-774">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-775">
                                  Company Name
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-776">
                                  Hammermill
                                </p>
                              </div>
                              <div data-id="element-777">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-778">
                                  Contact Name
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-779">
                                  Tom Halpert
                                </p>
                              </div>
                              <div data-id="element-780">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-781">
                                  Contact Phone Number
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-782">
                                  (215) 555-0188
                                </p>
                              </div>
                              <div data-id="element-783">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-784">
                                  Contact Work Email
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-785">
                                  t.halpert@hammermill.com
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-6" data-id="element-786">
                            <div className="flex items-center gap-2 mb-2" data-id="element-787">
                              <h5 className="font-bold text-gray-900" data-id="element-788">
                                Reference Completed
                              </h5>
                              <CheckCircle2 className="w-5 h-5 text-green-500" data-id="element-789" />
                            </div>
                            <p className="text-sm text-gray-600 mb-6" data-id="element-790">
                              Response received on March 27, 2026
                            </p>
                            <div className="grid grid-cols-2 gap-y-4 gap-x-12" data-id="element-791">
                              <div className="flex justify-between border-b border-gray-200 pb-2" data-id="element-792">
                                <span className="text-sm text-gray-500" data-id="element-793">
                                  Years as Customer
                                </span>
                                <span className="text-sm font-medium text-gray-900" data-id="element-794">
                                  12
                                </span>
                              </div>
                              <div className="flex justify-between border-b border-gray-200 pb-2" data-id="element-795">
                                <span className="text-sm text-gray-500" data-id="element-796">
                                  Credit Limit
                                </span>
                                <span className="text-sm font-medium text-gray-900" data-id="element-797">
                                  $75,000
                                </span>
                              </div>
                              <div className="flex justify-between border-b border-gray-200 pb-2" data-id="element-798">
                                <span className="text-sm text-gray-500" data-id="element-799">
                                  Current Balance
                                </span>
                                <span className="text-sm font-medium text-gray-900" data-id="element-800">
                                  $8,200
                                </span>
                              </div>
                              <div className="flex justify-between border-b border-gray-200 pb-2" data-id="element-801">
                                <span className="text-sm text-gray-500" data-id="element-802">
                                  Payment Habits
                                </span>
                                <span className="text-sm font-medium text-gray-900" data-id="element-803">
                                  Prompt / Within Terms
                                </span>
                              </div>
                              <div className="flex justify-between border-b border-gray-200 pb-2" data-id="element-804">
                                <span className="text-sm text-gray-500" data-id="element-805">
                                  Recommended
                                </span>
                                <span className="text-sm font-medium text-gray-900" data-id="element-806">
                                  Yes
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                {/* Sabre - Not Sent */}
                <div className="border border-gray-200 rounded-lg bg-white overflow-hidden" data-id="element-807">
                  <button onClick={() => setExpandedRef(expandedRef === 'Sabre' ? null : 'Sabre')} className="w-full p-4 flex justify-between items-center hover:bg-gray-50 transition-colors text-left" data-id="element-808">
                    <div data-id="element-809">
                      <h4 className="text-lg font-bold text-gray-900 mb-1.5" data-id="element-810">
                        Sabre
                      </h4>
                      <div className="flex items-center gap-4 text-sm text-gray-500" data-id="element-811">
                        <span className="flex items-center gap-1.5" data-id="element-812">
                          <Calendar className="w-4 h-4" data-id="element-813" />
                          Not Sent
                        </span>
                      </div>
                    </div>
                    {expandedRef === 'Sabre' ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-814" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-815" />}
                  </button>
                  <AnimatePresence data-id="element-816">
                    {expandedRef === 'Sabre' && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="border-t border-gray-100" data-id="element-817">
                        <div className="p-6 space-y-6" data-id="element-818">
                          <div data-id="element-819">
                            <div className="flex items-center gap-2 mb-4" data-id="element-820">
                              <User className="w-5 h-5 text-gray-700" data-id="element-821" />
                              <h5 className="font-bold text-gray-900" data-id="element-822">
                                Reference Information
                              </h5>
                              <Edit2 className="w-4 h-4 text-indigo-600 cursor-pointer" data-id="element-823" />
                            </div>
                            <div className="bg-gray-50 rounded-lg p-6 grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-824">
                              <div data-id="element-825">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-826">
                                  Company Name
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-827">
                                  Sabre
                                </p>
                              </div>
                              <div data-id="element-828">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-829">
                                  Company Address
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-830">
                                  221 Sabre Way, Tallahassee FL 32301
                                </p>
                              </div>
                              <div data-id="element-831">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-832">
                                  Contact Name
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-833">
                                  Gabe Lewis
                                </p>
                              </div>
                              <div data-id="element-834">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-835">
                                  Contact Work Email
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-836">
                                  g.lewis@sabre.com
                                </p>
                              </div>
                              <div data-id="element-837">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-838">
                                  Contact Phone Number
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-839">
                                  (850) 555-0177
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-6" data-id="element-840">
                            <h5 className="font-bold text-gray-900 mb-2" data-id="element-841">
                              Ready to Send
                            </h5>
                            <p className="text-sm text-gray-600 mb-4" data-id="element-842">
                              This trade reference request has not yet been sent
                              to the contact.
                            </p>
                            <button className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700 rounded-md text-sm font-medium transition-colors" data-id="element-843">
                              <Send className="w-4 h-4" data-id="element-844" />
                              SEND REFERENCE
                            </button>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-id="element-845">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-846">
                  Additional Trade References
                </h4>
                <p className="text-sm text-gray-500 mb-4" data-id="element-847">
                  No additional references requested
                </p>
                <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2" data-id="element-848">
                  <ExternalLink className="w-4 h-4" data-id="element-849" />
                  REQUEST ADDITIONAL TRADE REFERENCES
                </button>
              </div>
            </div>

            {/* Personal Guarantors */}
            <div className="p-6 border-b border-gray-200" data-id="element-850">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-851">
                Personal Guarantors
              </h3>
              <div className="space-y-4 mb-6" data-id="element-852">
                {/* Michael Scott - Completed */}
                <div className="border border-gray-200 rounded-lg bg-white overflow-hidden" data-id="element-853">
                  <button onClick={() => setExpandedRef(expandedRef === 'guarantor-scott' ? null : 'guarantor-scott')} className="w-full p-4 flex justify-between items-center hover:bg-gray-50 transition-colors text-left" data-id="element-854">
                    <div data-id="element-855">
                      <h4 className="text-lg font-bold text-gray-900 mb-1.5" data-id="element-856">
                        Michael Scott
                      </h4>
                      <div className="flex items-center gap-4 text-sm text-gray-500" data-id="element-857">
                        <span className="flex items-center gap-1.5" data-id="element-858">
                          <Calendar className="w-4 h-4" data-id="element-859" />
                          Completed on March 27, 2026
                        </span>
                        <span className="flex items-center gap-1.5" data-id="element-860">
                          <MessageSquare className="w-4 h-4" data-id="element-861" />1 email sent
                        </span>
                      </div>
                    </div>
                    {expandedRef === 'guarantor-scott' ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-862" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-863" />}
                  </button>
                  <AnimatePresence data-id="element-864">
                    {expandedRef === 'guarantor-scott' && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="border-t border-gray-100" data-id="element-865">
                        <div className="p-6 space-y-6" data-id="element-866">
                          <div data-id="element-867">
                            <div className="flex items-center gap-2 mb-4" data-id="element-868">
                              <User className="w-5 h-5 text-gray-700" data-id="element-869" />
                              <h5 className="font-bold text-gray-900" data-id="element-870">
                                Guarantor Information
                              </h5>
                              <Edit2 className="w-4 h-4 text-indigo-600 cursor-pointer" data-id="element-871" />
                            </div>
                            <div className="bg-gray-50 rounded-lg p-6 grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-872">
                              <div data-id="element-873">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-874">
                                  Full Name
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-875">
                                  Michael Scott
                                </p>
                              </div>
                              <div data-id="element-876">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-877">
                                  Title
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-878">
                                  Regional Manager
                                </p>
                              </div>
                              <div data-id="element-879">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-880">
                                  Email
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-881">
                                  m.scott@dundermifflin.com
                                </p>
                              </div>
                              <div data-id="element-882">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-883">
                                  Phone
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-884">
                                  (570) 555-0100
                                </p>
                              </div>
                              <div data-id="element-885">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-886">
                                  Address
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-887">
                                  1725 Slough Avenue, Scranton, PA 18503
                                </p>
                              </div>
                              <div data-id="element-888">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-889">
                                  SSN
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-890">
                                  ***-**-4927
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-6" data-id="element-891">
                            <div className="flex items-center gap-2 mb-2" data-id="element-892">
                              <h5 className="font-bold text-gray-900" data-id="element-893">
                                Guarantee Completed
                              </h5>
                              <CheckCircle2 className="w-5 h-5 text-green-500" data-id="element-894" />
                            </div>
                            <p className="text-sm text-gray-600 mb-6" data-id="element-895">
                              Personal guarantee signed on March 27, 2026
                            </p>
                            <div className="grid grid-cols-2 gap-y-4 gap-x-12" data-id="element-896">
                              <div className="flex justify-between border-b border-gray-200 pb-2" data-id="element-897">
                                <span className="text-sm text-gray-500" data-id="element-898">
                                  Guarantee Amount
                                </span>
                                <span className="text-sm font-medium text-gray-900" data-id="element-899">
                                  $50,000
                                </span>
                              </div>
                              <div className="flex justify-between border-b border-gray-200 pb-2" data-id="element-900">
                                <span className="text-sm text-gray-500" data-id="element-901">
                                  Date Signed
                                </span>
                                <span className="text-sm font-medium text-gray-900" data-id="element-902">
                                  March 27, 2026
                                </span>
                              </div>
                              <div className="flex justify-between border-b border-gray-200 pb-2" data-id="element-903">
                                <span className="text-sm text-gray-500" data-id="element-904">
                                  Guarantee Type
                                </span>
                                <span className="text-sm font-medium text-gray-900" data-id="element-905">
                                  Unlimited
                                </span>
                              </div>
                              <div className="flex justify-between border-b border-gray-200 pb-2" data-id="element-906">
                                <span className="text-sm text-gray-500" data-id="element-907">
                                  Expiration
                                </span>
                                <span className="text-sm font-medium text-gray-900" data-id="element-908">
                                  None
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-id="element-909">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-910">
                  Additional Personal Guarantors
                </h4>
                <p className="text-sm text-gray-500 mb-4" data-id="element-911">
                  No additional guarantors requested
                </p>
                <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2" data-id="element-912">
                  <Send className="w-4 h-4" data-id="element-913" />
                  REQUEST ADDITIONAL PERSONAL GUARANTORS
                </button>
              </div>
            </div>

            {/* Banking Information */}
            <div className="p-6 border-b border-gray-200" data-id="element-914">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-915">
                Banking Information
              </h3>
              <div className="grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-916">
                <div data-id="element-917">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-918">Bank Name</p>
                  <p className="text-gray-900" data-id="element-919">Scranton National Bank</p>
                </div>
                <div data-id="element-920">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-921">Contact</p>
                  <p className="text-gray-900" data-id="element-922">Robert Lipton</p>
                </div>
                <div data-id="element-923">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-924">Type of Account</p>
                  <p className="text-gray-900" data-id="element-925">Checking</p>
                </div>
                <div data-id="element-926">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-927">Account #</p>
                  <p className="text-gray-900" data-id="element-928">481726350</p>
                </div>
                <div data-id="element-929">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-930">Email</p>
                  <p className="text-gray-900" data-id="element-931">r.lipton@scrantonnational.com</p>
                </div>
                <div data-id="element-932">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-933">City</p>
                  <p className="text-gray-900" data-id="element-934">Scranton</p>
                </div>
                <div data-id="element-935">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-936">State</p>
                  <p className="text-gray-900" data-id="element-937">PA</p>
                </div>
                <div data-id="element-938">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-939">Phone #</p>
                  <p className="text-gray-900" data-id="element-940">570-555-0198</p>
                </div>
                <div data-id="element-941">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-942">Fax #</p>
                  <p className="text-gray-900" data-id="element-943">570-555-0199</p>
                </div>
              </div>
            </div>

            {/* Signature */}
            <div className="p-6" data-id="element-944">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-945">
                Signature
              </h3>

              <div className="border-t border-indigo-200 pt-4 mb-6" data-id="element-946">
                <h4 className="font-bold text-gray-900 mb-3" data-id="element-947">
                  Terms &amp; Conditions
                </h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4" data-id="element-948">
                  <p className="text-sm text-gray-700 leading-relaxed" data-id="element-949">
                    I understand that should my account become delinquent (terms
                    of sale are based upon date of invoice) and that if the
                    creditor elects to employ a third party to collect monies
                    due, my company will be responsible for not only the
                    original amount billed, but also all costs associated with
                    the collection efforts including but not limited to
                    reasonable attorney fees. Cash In Advance restrictions may
                    be placed on any past due account. Customer will notify the
                    creditor, in writing of any ownership changes or legal means
                    of doing business by the company under which credit was
                    established.
                  </p>
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-6" data-id="element-950">
                  By signing the below you give Dunder Mifflin Paper Company
                  Inc., permission to check your bank and trade references.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-951">
                <div data-id="element-952">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-953">Signature</p>
                  <div className="border border-gray-200 rounded-lg p-4 h-24 flex items-end" data-id="element-954">
                    <svg viewBox="0 0 300 80" className="w-56 h-16" data-id="element-955">
                      <path d="M10,60 C20,55 25,30 35,25 C45,20 50,50 60,45 C70,40 75,20 85,25 C95,30 100,55 110,50 C115,47 118,35 125,30 C132,25 140,40 150,35 C155,32 160,28 170,30 C180,32 185,45 195,40 C200,38 205,25 215,30 C220,33 225,45 230,42" fill="none" stroke="#1e3a8a" strokeWidth="2" strokeLinecap="round" data-id="element-956" />
                    </svg>
                  </div>
                </div>
                <div data-id="element-957">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-958">Printed Name</p>
                  <p className="text-gray-900" data-id="element-959">Dwight K. Schrute</p>
                </div>
                <div data-id="element-960">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-961">Title</p>
                  <p className="text-gray-900" data-id="element-962">Assistant Regional Manager</p>
                </div>
                <div data-id="element-963">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-964">Date</p>
                  <p className="text-gray-900" data-id="element-965">2026-03-27</p>
                </div>
              </div>
            </div>
          </div> : <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center text-gray-500" data-id="element-966">
            Content for {activeTab} tab coming soon.
          </div>}
      </div>
    </div>;
}